<script setup lang="ts">
import { useId } from "vue";
import type { TextareaHTMLAttributes, LabelHTMLAttributes, HTMLAttributes } from "vue";
import type { z } from "zod";
import { Textarea } from ".";
import { Label } from "@/components/ui/label";
import { useOptionalField } from "@/lib/validation";

export interface TextareaPlusProps {
  /**
   * Label for the textarea
   */
  label?: string;
  /**
   * Placeholder for the textarea
   */
  placeholder?: string;
  /**
   * ID of the textarea. If not provided, a unique ID will be generated.
   */
  id?: string;
  rows?: string | number;
  disabled?: boolean;
  required?: boolean;

  /**
   * VeeValidate field path (e.g. `description` or `items[0].text`). When set, the textarea registers itself
   * with the surrounding `useForm()` and shows that field's validation error. A v-model stays in sync with the
   * form value. Whether it is set is decided at setup; the path itself may change (e.g. indexed rows).
   */
  name?: string;
  /**
   * Field-level Zod schema. Makes the `name`d field standalone: it validates on its own and does NOT join a
   * surrounding `useForm()`. For fields that belong to a form, put the rules into the form's schema instead.
   */
  schema?: z.ZodType;

  /**
   * HTML Attributes/props that will be forwarded to the label element
   */
  labelProps?: LabelHTMLAttributes;
  /**
   * HTML Attributes/props that will be forwarded to the textarea element
   */
  textareaProps?: TextareaHTMLAttributes;
  /**
   * HTML Attributes/props that will be forwarded to error message/hint element
   */
  hintProps?: HTMLAttributes;
}

const modelValue = defineModel<string>();
const props = defineProps<TextareaPlusProps>();

const generatedId = useId();
const textareaId = props.id || generatedId;

const { value, errorMessage, onBlur } = useOptionalField(() => props.name, modelValue, props.schema);
</script>

<template>
  <div :id="`textarea-plus-${textareaId}`">
    <Label
      v-if="label"
      :for="textareaId"
      class="mb-1"
      v-bind="labelProps"
    >
      {{ label }}
      <span v-if="required" class="text-destructive" aria-hidden="true">*</span>
    </Label>

    <Textarea
      :model-value="value ?? ''"
      :id="textareaId"
      :name
      :placeholder
      :rows
      :disabled
      :required
      :aria-required="!!required"
      :aria-invalid="!!errorMessage"
      v-bind="textareaProps"
      @update:model-value="value = String($event)"
      @blur="onBlur"
    />

    <slot name="hint" :invalid="!!errorMessage" :message="errorMessage ?? null" :props="hintProps">
      <span
        v-if="errorMessage"
        v-bind="hintProps"
        class="text-destructive text-sm mt-1 block"
      >
        {{ errorMessage }}
      </span>
    </slot>
  </div>
</template>
